import { ContainerComponent, today } from 'AppReact';
import { MakeReadyTableRowWrapper, MakeReadyItemCellWrapper, Td, ElementWithPopupWrapper, PopupWrapper } from 'wrappers';
import { Empty, WhiteTextSm } from 'atoms';
import { GreenTickSvg } from 'components/icons';
import { isBefore, format } from 'date-fns';
import React from 'react';
import Controller from './controller';
import internalState from './internalState';
import MakeReadyDateCell from '../makeReadyDateCell';
import MakeReadyPropertyCell from '../makeReadyPropertyCell';
import columnSwitch from './columnSwitch';
import { formatNumberToCurrency } from 'utils';

const containerRef = React.createRef();

export const definition = {
  name: 'RowContainer',
  controller: Controller,
  initialProps: ({
    card,
    makeReadyCategories,
    techs,
    vendors,
    activeAdmins,
    completedUnitsIds,
    completedItemsIds,
    archivedUnitsIds,
    selectedItems,
    selectedCategories,
    inspectionTemplates
  }) => ({
    card,
    inspectionTemplates,
    makeReadyCategories,
    techs,
    vendors,
    activeAdmins,
    completedUnitsIds,
    completedItemsIds,
    archivedUnitsIds,
    selectedItems,
    selectedCategories
  }),
  props: internalState,
  elements: [
    {
      wrapper: MakeReadyTableRowWrapper,
      name: 'Row',
      displayState: () => ({
        isSelected: false,
        disabledReadyDate: window.settings?.use_integration_for_make_ready_date
      }),
      elements: [
        {
          component: MakeReadyPropertyCell,
          name: 'PropertyCell',
          renderIf: ({ completedUnitsIds, card }) => !completedUnitsIds?.includes(card.id),
          bindings: ({ card, isSelected, exceptions }) => ({
            exceptions,
            card,
            unit: card?.unit?.number,
            property: card?.property?.name,
            isSelected
          })
        },
        {
          component: MakeReadyDateCell,
          renderIf: ({ completedUnitsIds, card }) => !completedUnitsIds?.includes(card.id),
          name: 'MoveOutCell',
          bindings: ({ card }) => ({
            selectedDate: card.move_out_date,
            type: 'move-out',
            showCheckMark: card.unit.occupancy_status !== 'notice'
          })
        },
        {
          component: MakeReadyDateCell,
          renderIf: ({ completedUnitsIds, card }) => !completedUnitsIds?.includes(card.id),
          name: 'ReadyCell',
          bindings: ({ card }) => ({
            selectedDate: card.deadline,
            type: 'ready',
            showCheckMark: card.completed_on
          })
        },
        {
          component: MakeReadyDateCell,
          renderIf: ({ completedUnitsIds, card }) => !completedUnitsIds?.includes(card.id),
          name: 'MoveInCell',
          bindings: ({ card }) => ({ selectedDate: card.move_in_date, type: 'move-in' })
        },
        {
          componentSelect: columnSwitch,
          name: 'ItemCell',
          isListOf: 'makeReadyCategories',
          renderIf: ({
            makeReadyCategory,
          }) => makeReadyCategory.active,
          bindings: ({
            makeReadyCategory,
            card,
            techs,
            vendors,
            activeAdmins,
            completedItemsIds,
            selectedItems,
            selectedCategories,
            inspectionTemplates,
            makeReadyCategories
          }) => {
            const item = card.items.find((item) => item.name === makeReadyCategory.name);
            const isDisabled = card.disabled_categories?.includes(makeReadyCategory.name);
            let data = card.card_data.find((cd) => cd.category_id === makeReadyCategory.id);

            if (makeReadyCategory.type === 'area') {
              data = { data: card.unit.area?.toString() ?? '-' };
            }
            if (makeReadyCategory.type === 'days_vacant') {
              data = { data: card.unit.days_vacant?.toString() ?? '-' };
            }
            if (makeReadyCategory.type === 'floor_plan') {
              data = { data: card.unit.floor_plan ?? '-' };
            }
            if (makeReadyCategory.type === 'on_notice_resident_name') {
              data = { data: card.unit.on_notice_resident_name ?? '-' };
            }
            if (makeReadyCategory.type === 'on_notice_resident_phone') {
              data = { data: card.unit.on_notice_resident_phone ?? '-' };
            }
            if (makeReadyCategory.type === 'marketRent') {
              data = { data: card.unit.market_rent ? formatNumberToCurrency({value: card.unit.market_rent}) : '-' };
            }
            if (makeReadyCategory.type === 'newResidentPhoneNumber') {
              data = { data: card.unit.new_resident_phone ?? '-' };
            }
            if (makeReadyCategory.type === 'newResidentName') {
              data = { data: card.unit.new_resident_name ?? '-' };
            }

            return {
              activeAdmins,
              inspectionTemplates,
              column: isDisabled ? { type: 'N/A' } : makeReadyCategory,
              item,
              card,
              data,
              selectedItems,
              makeReadyCategories,
              makeReadyCategory,
              containerRef,
              selectedRow: false,
              isSelected: !!selectedItems.find((i) => i.card_id === card.id && i.make_ready_category_id === makeReadyCategory.id),
              shouldOpen: !selectedItems.length,
              hideCheckbox: hideCheckboxFn(item, selectedItems, makeReadyCategory.id, selectedCategories),
              showCheckboxes: !!selectedItems.length > 0 && (selectedCategories.find(({ category_id }) => category_id === makeReadyCategory.id) || selectedCategories.length === 0),
              displayConfetti: !!completedItemsIds.find((c) => c.card_id === card.id && c.category_id === makeReadyCategory.id),
              techs: techs.filter((t) => t.property_ids.includes(card.property.id)),
              vendors: vendors.filter((v) => v.property_ids.includes(card.property.id)),
              admins: activeAdmins.filter((a) => a.property_ids.includes(card.property.id))
            };
          }
        },
        {
          wrapper: Td,
          isButton: true,
          name: 'OfficeSignOffCell',
          bindings: ({ card }) => ({
            card,
            disabled: window.settings?.use_integration_for_make_ready_completion
          }),
          elements: [
            {
              wrapper: ElementWithPopupWrapper,
              name: 'PopupWrapper',
              elements: [
                {
                  wrapper: MakeReadyItemCellWrapper,
                  name: 'Wrapper',
                  displayState: ({ card }) => {
                    let background = '';
                    if (window.settings?.use_integration_for_make_ready_completion) {
                      if (card.completed_on) {
                        background = '#C7EFDA';
                      } else {
                        background = '#EEEEEE';
                      }
                    } else if (card.hidden) {
                      background = '#C7EFDA';
                    } else if (card.completed_on) background = '#1DBD6B';
                    return { background };
                  },
                  elements: [
                    {
                      component: Empty,
                      name: 'Icon',
                      displayValue: ({ card }) => GreenTickSvg({
                        width: 16,
                        height: 16,
                        color: card.completed_on ? '#FFFFFF' : '#9EACB0'
                      })
                    }
                  ]
                },
                {
                  wrapper: PopupWrapper,
                  name: 'PopupWrapper',
                  renderIf: ({ card }) => window.settings?.use_integration_for_make_ready_completion && !card.completed_on,
                  displayState: ({ }) => ({ position: 'left' }),
                  elements: [
                    {
                      component: WhiteTextSm,
                      name: 'Name',
                      displayValue: ({ }) => 'Please complete this unit in your management system'
                    }
                  ]
                },
                {
                  wrapper: PopupWrapper,
                  name: 'PopupWrapper',
                  renderIf: ({ card }) => card.completed_on,
                  displayState: ({ }) => ({ position: 'left' }),
                  elements: [
                    {
                      component: WhiteTextSm,
                      name: 'Name',
                      displayValue: ({ card }) => `${format(card.completed_on, 'MM/dd/yyyy')}`
                    }
                  ]
                }
              ]
            }
          ]
        }
      ]
    }
  ]
};

const hideCheckboxFn = (item, selectedItems, categoryId, selectedCategories) => {
  if (selectedCategories.find(({ category_id }) => category_id === categoryId)) return false;
  if (item?.completed && selectedItems.find((i) => i.is_complete)) return false;
  if ((!!item?.tech_id || !!item?.admin_id || !!item?.vendor_id || !!item?.scheduled) && !item?.completed && selectedItems.find((i) => (i.is_assigned || i.is_scheduled) && !i.is_complete)) return false;
  if (!item?.tech_id && !item?.admin_id && !item?.vendor_id && !item?.completed && !item?.scheduled && selectedItems.find((i) => !i.is_assigned && !i.is_scheduled && !i.is_complete)) return false;

  return true;
};

export default ContainerComponent(definition);
